import { QualityEditWarning } from './QualityEditWarning'
import { publicationState } from './publicationState'
import { RubricComparison, shownImplementedRubric } from './RubricComparison'
import { RubricLinkProvider, revealRubricCheck } from './rubricLinks'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { api, ApiError } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { useAuth } from '@/auth/AuthContext'
import { QualityPrompt, QualityBrief } from './QualityContent'
import { WorkReview } from './WorkReview'
import { BuilderSubmission } from './BuilderSubmission'
import { validate } from '@/lib/schema'
import { fmtDateTime } from '@/lib/format'
import { saveUrl } from '@/lib/zip'
import type { Role } from '@/lib/types'
import type { ReviewDetail } from './types'
import { ReviewRollouts } from './ReviewRollouts'
import { EngineerDecision } from './EngineerDecision'
import { ReviewFeedback } from './ReviewFeedback'
import { ExpertReview } from './ExpertReview'
import { PersonName } from '@/components/PersonName'
import { RecordingPlayer } from './RecordingPlayer'

export function ReviewPanel({
  taskId,
  roundId,
  role,
  onChanged,
  standalone = false,
  inTask = false,
  active = true,
  decisionTarget,
  reviewPicker,
  sidebarFooter,
  onDirty,
  readOnly = false,
  decisionBlocked = false,
}: {
  taskId: string
  roundId: string
  role: Role
  onChanged?: () => void
  standalone?: boolean
  inTask?: boolean
  active?: boolean
  decisionTarget?: HTMLElement | null
  reviewPicker?: ReactNode
  sidebarFooter?: ReactNode
  onDirty?: (dirty: boolean) => void
  readOnly?: boolean
  decisionBlocked?: boolean
}) {
  const { user } = useAuth()
  const base = `/tasks/${taskId}/reviews/${roundId}`
  const { data, error, loading, reload } = useAsync(() => api.get<ReviewDetail>(base), [base])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [section, setSection] = useState('rollouts')
  const [watching, setWatching] = useState('')
  const [recordingError, setRecordingError] = useState('')
  const [peerDirty, setPeerDirty] = useState(false)
  const [newFeedback, setNewFeedback] = useState(false)
  useEffect(() => { onDirty?.(!readOnly && (peerDirty || busy)); return () => onDirty?.(false) }, [peerDirty, busy, readOnly, onDirty])
  // A rollout result opens its check in the Submission section's rubrics.
  const materials = useRef<HTMLDivElement>(null)
  const openRubricCheck = useCallback((checkId: string) => {
    setSection('submission')
    revealRubricCheck(checkId, () => materials.current)
  }, [])
  useEffect(() => {
    if (!peerDirty || !data || loading) return
    let disposed = false
    let pending = false
    const timer = window.setInterval(() => {
      if (pending) return
      pending = true
      void api.get<ReviewDetail>(base).then(latest => {
        if (!disposed && latest.round.version !== data.round.version) setNewFeedback(true)
      }).catch(() => {}).finally(() => { pending = false })
    }, 5000)
    return () => { disposed = true; window.clearInterval(timer) }
  }, [peerDirty, base, data?.round.version, loading])
  useEffect(() => {
    // A slow poll must finish before the next one can supersede its result.
    if (busy || peerDirty || loading) return
    const timer = window.setInterval(reload, 5000)
    return () => window.clearInterval(timer)
  }, [reload, busy, peerDirty, loading])
  async function action(path: string, body?: unknown) {
    const schema = path.endsWith('/recommendations') ? 'review_recommendation' : null
    const issues = schema ? validate(schema, body) : []
    if (issues.length) {
      setFailure(issues.map((issue) => issue.message).join(' '))
      return false
    }
    setBusy(true)
    setFailure('')
    try {
      await api.post(path, body)
      reload()
      onChanged?.()
      return true
    } catch (e) {
      setFailure((e as Error).message)
      // Polling pauses while feedback is drafted. Refresh a stale version without
      // remounting the form or discarding what the reviewer has written.
      if (e instanceof ApiError && e.status === 409) reload()
    } finally {
      setBusy(false)
    }
  }
  if (loading && !data) return <p className="muted">Loading review…</p>
  if (!data) return <div className="note err">{error}</div>
  const view = readOnly ? { ...data, is_current: false, can_decide: false, can_recommend: false } : data
  const { round, submission } = view
  const task = submission.task
  const isPeer = round.peer_id === user?.id
  const open = round.status === 'open' && view.is_current
  const preparing = round.status === 'preparing'
  const visibleSection = preparing && section === 'rollouts' ? 'submission' : section
  if (!readOnly && round.status === 'deciding') return <section className="box" aria-live="polite">
    <h3>{round.handoff_error ? 'Submission needs attention' : 'Submitting review'}</h3>
    <p>{round.handoff_error ?? 'Your decision is saved. Moving the task to its next stage…'}</p>
    {round.handoff_error && (isPeer || role === 'engineer') && <button disabled={busy} onClick={() => action(`${base}/retry-handoff`, {})}>Retry submission</button>}
    {failure && <p role="alert">{failure}</p>}
  </section>
  const preparationNotice = preparing ? (
    <section className="box" aria-live="polite">
      <h3>Preparing review</h3>
      <p className="muted">
        {data.agent_review?.status === 'failed'
          ? 'The evidence could not be prepared. A developer can retry preparation.'
          : 'The submission is available below. Rollouts and decisions will appear when preparation finishes.'}
      </p>
      {role === 'engineer' && data.agent_review?.status === 'failed' && (
        <p className="muted small">{data.agent_review.error}</p>
      )}
      {!readOnly && role === 'engineer' && data.agent_review?.status === 'failed' && (
        <button
          className="ghost"
          disabled={busy}
          onClick={() => action(`${base}/retry-preparation`, {})}
        >
          Retry preparation
        </button>
      )}
      {failure && <p className="note err">{failure}</p>}
    </section>
  ) : null
  const link = async (id: string) => (await api.get<{ url: string }>(`${base}/files/${id}`)).url
  /** Recording downloads report next to the player, not in the workspace's general message. */
  async function downloadRecording(id: string) {
    setRecordingError('')
    try {
      saveUrl(await link(id))
    } catch (e) {
      setRecordingError((e as Error).message)
    }
  }
  async function file(id: string) {
    try {
      saveUrl(await link(id))
    } catch (e) {
      setFailure((e as Error).message)
    }
  }
  const referenceWarnings = submission.handoff?.warnings?.map((warning) => (
    <p className="note" role="status" key={warning}>{warning}</p>
  ))
  if (round.kind === 'submission' || role === 'sme')
    return (
      <>
        {!inTask && <QualityEditWarning changes={view.quality_changes} />}
        {preparationNotice}
        {referenceWarnings}
        <ExpertReview
          key={`${round.round_id}:${user?.id}`}
          data={view}
          role={role}
          standalone={standalone}
          inTask={inTask}
          active={active}
          decisionTarget={decisionTarget}
          reviewPicker={reviewPicker}
          busy={busy || (loading && !!failure)}
          failure={failure}
          action={action}
          onDirty={setPeerDirty}
        />
      </>
    )
  const reviewFeedback = (
    <ReviewFeedback data={data} role={role} open={open} busy={busy} base={base} action={action} />
  )
  return (
    <div className="review-workspace">
      {!inTask && <QualityEditWarning changes={view.quality_changes} />}
      {preparationNotice}
      {referenceWarnings}
      {newFeedback && <p className="note">New feedback arrived.
        <button className="link" onClick={() => { reload(); setNewFeedback(false) }}>Review latest feedback</button>
      </p>}
      <div className="review-heading">
        <div>
          <h2>{round.kind === 'quality' ? 'Quality review' : 'Expert submission review'}</h2>
          <p className="muted small">
            Submitted {fmtDateTime(round.created_at)}
            {round.peer_email ? <> · Peer: <PersonName people={view.people} id={round.peer_id} email={round.peer_email} /></>
              : open ? ' · Waiting for a peer' : ''}
          </p>
        </div>
        <span className={`chip ${data.is_current && data.publication ? publicationState(data.publication.status).tone : open ? 'neutral' : 'ok'}`}>
          {data.is_current && data.publication ? publicationState(data.publication.status).label : round.status === 'publishing' ? 'Publication awaiting confirmation' : round.status}
        </span>
      </div>
      {!data.is_current && (
        <p className="note">
          This is a previous review round. Its submission and feedback have been preserved.
        </p>
      )}
      <div
        className={
          inTask && (data.can_decide || data.can_recommend) ? 'peer-review-layout with-decision' : ''
        }
      >
        <div className="peer-review-main" ref={materials}>
          {inTask ? (
            <WorkReview role={role} data={data} active={active} feedback={reviewFeedback} />
          ) : (
            <RubricLinkProvider spec={shownImplementedRubric(task, data.submission.implemented_rubric)} open={openRubricCheck}>
              <div className="review-material-tabs" role="group" aria-label="Review materials">
                {['submission', ...(round.kind === 'quality' && !preparing ? ['rollouts'] : []), 'feedback'].map(
                  (value) => (
                    <button
                      key={value}
                      className={visibleSection === value ? 'primary sm' : 'ghost sm'}
                      onClick={() => setSection(value)}
                    >
                      {value === 'submission'
                        ? 'Submission'
                        : value === 'rollouts'
                          ? 'Rollouts'
                          : 'Feedback'}
                    </button>
                  ),
                )}
              </div>
              {visibleSection === 'submission' && (
                <>
                  {submission.files.some((f) => f.kind === 'video') && (
                    <section className="box">
                      <div className="panel-section-head">
                        <h3>Expert execution</h3>
                        {watching && (
                          <button className="link small" aria-label={`Download ${submission.files.find((f) => f.file_id === watching)?.filename ?? 'the recording'}`}
                            onClick={() => void downloadRecording(watching)}>
                            Download
                          </button>
                        )}
                      </div>
                      <div className="row">
                        {submission.files
                          .filter((f) => f.kind === 'video')
                          .map((f) => (
                            <button key={f.file_id} aria-pressed={watching === f.file_id} onClick={() => { setWatching(f.file_id); setRecordingError('') }}>
                              Watch {f.filename}
                            </button>
                          ))}
                      </div>
                      {watching && (
                        <RecordingPlayer key={`${base}/${watching}`} load={() => link(watching)} onDownload={() => void downloadRecording(watching)} active={active} />
                      )}
                      {recordingError && <p className="note err" role="alert">{recordingError}</p>}
                    </section>
                  )}
                  <section className="box">
                    <h3>Agent prompt</h3>
                    <QualityPrompt prompt={task.prompt} changes={view.quality_changes} />
                  </section>
                  {!task.scientific && <section className="box">
                    <h3>Task brief</h3>
                    <QualityBrief brief={task.brief} changes={view.quality_changes} />
                  </section>}
                  <details className="box" open>
                    <summary>Rubrics</summary>
                    <RubricComparison task={task} role={role} implementedRubric={data.submission.implemented_rubric} />
                  </details>
                  <details className="box">
                    <summary>Files and execution notes ({submission.files.length})</summary>
                    <p className="prewrap">{task.submission?.notes}</p>
                    <ul className="filelist">
                      {submission.files.map((f) => (
                        <li key={f.file_id}>
                          <button className="link" onClick={() => file(f.file_id)}>
                            {f.filename}
                          </button>
                          <span className="small muted">{f.kind.replaceAll('_', ' ')}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  <BuilderSubmission task={task} role={role} />
                </>
              )}
              {section === 'rollouts' && !preparing && <ReviewRollouts taskId={taskId} roundId={roundId} />}
              {section === 'feedback' && reviewFeedback}
            </RubricLinkProvider>
          )}
        </div>
        <div className={inTask ? 'review-engineer-sidebar' : ''}>
          {(view.can_decide || (view.can_recommend && open)) && (
            <EngineerDecision
              key={`${roundId}:${user?.id}`}
              data={data}
              busy={busy || decisionBlocked || (loading && !!failure)}
              action={action}
              onDirty={setPeerDirty}
            />
          )}
          {data.is_current && data.publication && data.publication.status !== 'published' && (
            <p className="note">{publicationState(data.publication.status).explanation}</p>
          )}
          {sidebarFooter}
        </div>
      </div>
      {isPeer && open && (
        <div className="row">
          <button
            className="ghost small"
            disabled={busy}
            onClick={async () => {
              if (await action(`${base}/release`)) location.assign('/sme/reviews')
            }}
          >
            Release review
          </button>
        </div>
      )}
      {failure && (
        <p role="alert" className="note err">
          {failure}
        </p>
      )}
    </div>
  )
}
